import React from 'react';
import { Avatar, Box, Button, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import 'katex/dist/katex.min.css';
import AIAgentThinkingIndicator from './AIAgentThinkingIndicator';
import { AIAgentAvatarStarsWhiteIcon, AIAgentErrorIcon } from './AIAgentIcons';
import { AI_AGENT_TOKENS } from '../AIAgentConstants';
import { AIAgentMessage } from '../AIAgentTypes';

interface Props {
  message: AIAgentMessage;
  onRetry?: (questionId: string) => void;
  onStartNewConversation?: () => void;
}

const markdownSanitizeSchema = {
  ...defaultSchema,
  tagNames: (defaultSchema.tagNames || []).filter((tag) => tag !== 'a'),
  attributes: {
    ...defaultSchema.attributes,
    code: [
      ...(defaultSchema.attributes?.code || []),
      ['className', /^language-./, 'math-inline', 'math-display'],
    ],
    span: [...(defaultSchema.attributes?.span || []), ['className', /^katex/]],
    div: [...(defaultSchema.attributes?.div || []), ['className', /^katex/]],
  },
};

const markdownComponents = {
  a: ({ children }: { children?: React.ReactNode }) => (
    <span>{children}</span>
  ),
};

function AssistantMarkdown({ content }: { content: string }) {
  return (
    <Box
      data-testid="ai-agent-markdown"
      sx={{
        fontFamily: AI_AGENT_TOKENS.fontFamily,
        fontSize: 14,
        lineHeight: '19.5px',
        wordBreak: 'break-word',
        '& p': { margin: 0, marginBottom: 1 },
        '& p:last-child': { marginBottom: 0 },
        '& ul, & ol': { margin: 0, paddingLeft: 2.5, marginBottom: 1 },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          marginBottom: 1,
          display: 'block',
          overflowX: 'auto',
        },
        '& th, & td': {
          border: `1px solid ${AI_AGENT_TOKENS.borderSubtle}`,
          padding: '4px 8px',
          textAlign: 'left',
        },
        '& pre': {
          overflowX: 'auto',
          backgroundColor: AI_AGENT_TOKENS.surface,
          padding: 1,
          borderRadius: 1,
        },
        '& code': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.9em',
        },
        '& .katex': { fontSize: '1em' },
      }}
    >
      <ReactMarkdown
        skipHtml
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[
          [rehypeSanitize, markdownSanitizeSchema],
          rehypeKatex,
        ]}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
}

export default function AIAgentMessageBubble({
  message,
  onRetry,
  onStartNewConversation,
}: Props) {
  if (message.role === 'welcome') {
    return null;
  }

  const isUser = message.role === 'user';
  const isError = message.role === 'error';
  const isThinking = message.state === 'thinking';
  const isAssistantSide = !isUser;
  const isAssistantMarkdown =
    message.role === 'assistant' && message.state === 'settled';

  const bubble = (
    <Box
      sx={{
        maxWidth: isUser ? '85%' : '100%',
        px: isUser ? '13px' : '14px',
        py: isUser ? 1 : 1.5,
        borderRadius: isUser
          ? '14px 14px 3px 14px'
          : '3px 12px 12px 12px',
        backgroundColor: isUser ? AI_AGENT_TOKENS.primary : AI_AGENT_TOKENS.white,
        color: isUser ? AI_AGENT_TOKENS.white : AI_AGENT_TOKENS.textBody,
        border: isError
          ? '1px solid #d32f2f'
          : isAssistantSide
            ? `1px solid ${AI_AGENT_TOKENS.borderSubtle}`
            : 'none',
        boxShadow: isAssistantSide
          ? '0px 1px 2px rgba(0, 0, 0, 0.06)'
          : 'none',
      }}
    >
      {isThinking ? (
        <AIAgentThinkingIndicator />
      ) : isError && message.action === 'retry' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <AIAgentErrorIcon size={14} />
            <Typography
              sx={{
                fontFamily: AI_AGENT_TOKENS.fontFamily,
                fontWeight: 600,
                fontSize: 14,
                lineHeight: '18.75px',
                color: AI_AGENT_TOKENS.textBody,
              }}
            >
              Não consegui responder agora.
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: AI_AGENT_TOKENS.fontFamily,
              fontWeight: 400,
              fontSize: 14,
              lineHeight: '18px',
              color: 'rgba(0, 0, 0, 0.5)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {message.content}
          </Typography>
        </Box>
      ) : isAssistantMarkdown ? (
        <AssistantMarkdown content={message.content} />
      ) : (
        <Typography
          sx={{
            fontFamily: AI_AGENT_TOKENS.fontFamily,
            fontSize: 14,
            lineHeight: '19.5px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message.content}
        </Typography>
      )}

      {isError && message.action === 'retry' && message.questionId && onRetry && (
        <Button
          size="small"
          variant="contained"
          onClick={() => onRetry(message.questionId!)}
          sx={{
            mt: 1.25,
            px: 1.25,
            py: 0.5,
            minWidth: 0,
            borderRadius: `${AI_AGENT_TOKENS.borderRadius}px`,
            backgroundColor: AI_AGENT_TOKENS.primary,
            fontFamily: AI_AGENT_TOKENS.fontFamily,
            fontWeight: 500,
            fontSize: 13,
            lineHeight: '22px',
            letterSpacing: '0.46px',
            textTransform: 'uppercase',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: AI_AGENT_TOKENS.primary,
              boxShadow: 'none',
              opacity: 0.92,
            },
          }}
        >
          Tentar novamente
        </Button>
      )}

      {isError &&
        message.action === 'start-new-conversation' &&
        onStartNewConversation && (
          <Button
            size="small"
            variant="contained"
            onClick={onStartNewConversation}
            sx={{
              mt: 1.25,
              px: 1.25,
              py: 0.5,
              minWidth: 0,
              borderRadius: `${AI_AGENT_TOKENS.borderRadius}px`,
              backgroundColor: AI_AGENT_TOKENS.primary,
              fontFamily: AI_AGENT_TOKENS.fontFamily,
              fontWeight: 500,
              fontSize: 13,
              lineHeight: '22px',
              letterSpacing: '0.46px',
              textTransform: 'uppercase',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: AI_AGENT_TOKENS.primary,
                boxShadow: 'none',
                opacity: 0.92,
              },
            }}
          >
            Iniciar nova conversa
          </Button>
        )}
    </Box>
  );

  if (isUser) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 1.25,
        }}
      >
        {bubble}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        mb: 1.25,
        pt: '10px',
      }}
    >
      <Avatar
        sx={{
          width: 24,
          height: 24,
          backgroundColor: AI_AGENT_TOKENS.primary,
          flexShrink: 0,
        }}
      >
        <AIAgentAvatarStarsWhiteIcon size={16} />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>{bubble}</Box>
    </Box>
  );
}
